import { useMemo } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { DashboardCard } from './DashboardCard';
import { VacationTable } from './VacationTable';
import { PeriodPicker } from './PeriodPicker';
import type { PersonRow, PeopleFilters, Status } from '../../../utils/panorama/panoramaPeopleRows';
import { countByStatus, buildPeopleRows } from '../../../utils/panorama/panoramaPeopleRows';
import { STATUS_OPTIONS, STATUS_META } from '../vacationOverviewPage.definitions';
import { MultiSelectFilter } from './MultiSelectFilter';

const STAGE_LABELS: Record<string, string> = {
	manager: 'Gestor',
	client: 'Cliente',
	dp: 'DP',
	correction: 'Correção',
};

interface TableScreenProps {
	rows: PersonRow[];
	filters: PeopleFilters;
	onFiltersChange: (filters: PeopleFilters) => void;
	allRows: PersonRow[];
	clientOptions: Array<{ name: string }>;
	allDocsRows: PersonRow[];
	referenceDate: Date;
	minDate?: string;
}

export function TableScreen({ rows, filters, onFiltersChange, allRows, clientOptions, allDocsRows, referenceDate, minDate }: TableScreenProps) {
	const filteredBySearchAndClient = useMemo(() => {
		return allRows.filter(row => {
			if (filters.search && filters.search.trim()) {
				const searchLower = filters.search.toLowerCase();
				if (!row.name.toLowerCase().includes(searchLower)) return false;
			}

			if (filters.clients && filters.clients.length > 0) {
				const hasClient = filters.clients.some(c => row.clientNames.includes(c));
				if (!hasClient) return false;
			}

			return true;
		});
	}, [allRows, filters]);

	const statusCounts = useMemo(() => countByStatus(filteredBySearchAndClient), [filteredBySearchAndClient]);

	const statusCountsForChips = useMemo(() => {
		const baseFilters = { search: filters.search, clients: filters.clients };
		return countByStatus(allRows.filter(row => {
			if (baseFilters.search && baseFilters.search.trim()) {
				const searchLower = baseFilters.search.toLowerCase();
				if (!row.name.toLowerCase().includes(searchLower)) return false;
			}
			if (baseFilters.clients && baseFilters.clients.length > 0) {
				const hasClient = baseFilters.clients.some(c => row.clientNames.includes(c));
				if (!hasClient) return false;
			}
			return true;
		}));
	}, [allRows, filters.search, filters.clients]);

	const countForStatus = (status: Status) => {
		return statusCountsForChips[status] ?? 0;
	};

	const PRESET_CLASS = 'w-full rounded-lg border border-raro-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300';

	return (
		<div className="space-y-4 p-3 md:p-6">
			<DashboardCard title="Férias" subtitle="Período de férias de cada pessoa">
				<div className="space-y-4">
					<div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-tour="overview-table-filters">
						<div>
							<label className="mb-1 block text-xs font-medium text-raro-gray-600">Busca</label>
							<input
								type="text"
								placeholder="Nome..."
								value={filters.search || ''}
								onChange={e => onFiltersChange({ ...filters, search: e.target.value })}
								className={PRESET_CLASS}
							/>
						</div>

						<div>
							<label className="mb-1 block text-xs font-medium text-raro-gray-600">Cliente</label>
							<MultiSelectFilter
								label="Cliente"
								options={clientOptions.map(c => ({ value: c.name, label: c.name }))}
								values={filters.clients || []}
								onChange={values => onFiltersChange({ ...filters, clients: values })}
							/>
						</div>

						<div className="contents">
							<PeriodPicker
								from={filters.periodFrom || null}
								to={filters.periodTo || null}
								onChange={(from, to) => onFiltersChange({ ...filters, periodFrom: from, periodTo: to })}
								today={referenceDate}
								allowEmpty={true}
								minDate={minDate}
							/>
						</div>
					</div>

					<div className="space-y-2">
						<label className="block text-xs font-medium text-raro-gray-600">Situação</label>
						<div className="flex flex-nowrap gap-2 overflow-x-auto whitespace-nowrap md:flex-wrap md:overflow-visible -mx-3 px-3 md:mx-0 md:px-0" data-tour="overview-table-status">
							<button
								onClick={() => onFiltersChange({ ...filters, statuses: [] })}
								className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
									!filters.statuses || filters.statuses.length === 0
										? 'bg-primary-600 text-white'
										: 'bg-raro-gray-100 text-raro-gray-700 hover:bg-raro-gray-200'
								}`}
							>
								Todos {statusCounts.all}
							</button>

							{STATUS_OPTIONS.map(opt => {
								const count = countForStatus(opt.value as Status);
								const isActive = filters.statuses?.includes(opt.value as Status);
								const meta = STATUS_META[opt.value as Status];
								return (
									<button
										key={opt.value}
										onClick={() => {
											const next = isActive
												? (filters.statuses || []).filter(s => s !== opt.value)
												: [...(filters.statuses || []), opt.value as Status];
											onFiltersChange({ ...filters, statuses: next });
										}}
										className="rounded-full px-3 py-1 text-xs font-semibold transition"
										style={isActive ? {
											backgroundColor: meta.color,
											color: 'white',
										} : {
											backgroundColor: '#f3f4f6',
											color: '#374151',
										}}
									>
										<span
											className="mr-1 inline-block h-2 w-2 rounded-full"
											style={{ backgroundColor: isActive ? 'white' : meta.color }}
										/>
										{opt.label} {count}
									</button>
								);
							})}
						</div>

						<div className="flex flex-wrap gap-2">
							{filters.prazoMes && (
								<div className="flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs text-blue-700">
									Prazo: {filters.prazoMes}
									<button
										onClick={() => onFiltersChange({ ...filters, prazoMes: null })}
										className="ml-1 hover:text-blue-900"
									>
										<XMarkIcon className="h-3 w-3" />
									</button>
								</div>
							)}
							{filters.stages && filters.stages.length > 0 && (
								filters.stages.map(stage => (
									<div key={stage} className="flex items-center gap-1 rounded-full bg-purple-100 px-3 py-1 text-xs text-purple-700">
										{STAGE_LABELS[stage] || stage}
										<button
											onClick={() => onFiltersChange({ ...filters, stages: filters.stages?.filter(s => s !== stage) || null })}
											className="ml-1 hover:text-purple-900"
										>
											<XMarkIcon className="h-3 w-3" />
										</button>
									</div>
								))
							)}
						</div>
					</div>

					{(filters.search || (filters.clients && filters.clients.length > 0) || filters.periodFrom || filters.periodTo || filters.prazoMes || (filters.stages && filters.stages.length > 0)) && (
						<button
							onClick={() => onFiltersChange({ search: '', clients: [], periodFrom: null, periodTo: null, prazoMes: null, stages: null, statuses: [] })}
							className="flex items-center gap-1 text-xs font-medium text-raro-gray-600 hover:text-raro-gray-900"
						>
							<XMarkIcon className="h-3.5 w-3.5" />
							Limpar filtros
						</button>
					)}
				</div>

				<div className="mt-6" data-tour="overview-table">
					<VacationTable rows={rows} />
				</div>
			</DashboardCard>
		</div>
	);
}
